import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import {
  AdvisorSelectionActions,
  advisorSelectionModule,
} from '@mse-reg/app/modules/selections/advisor-selection.slice';
import {
  AdvisorDto,
  hasAnyRole,
  IAccountClient,
  IAdvisorClient,
  IUasClient,
  IUasDomainClient,
  resolver,
  RoleType,
  UasDomainDto,
  UasDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authThunks } from '../login/auth.slice';

// State
const initialState = {
  loading: false,
  selectedUas: null as UasDto | null,
  domainsLoading: false,
  domains: [] as UasDomainDto[],
  domainsForUas: {} as { [id: number]: UasDomainDto[] },
  selectedDomain: null as UasDomainDto | null,
  advisorsLoading: false,
  selectedAdvisor: null as AdvisorDto | null,
  advisorsWithNoUasRelation: [] as AdvisorDto[],
};

export type UasManagementState = Readonly<typeof initialState>;

// Slice
const slice = createSlice({
  name: 'uasManagement',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSelectedUas(state, action: PayloadAction<UasDto | null>) {
      state.selectedUas = action.payload;
    },
    setDomainsLoading(state, action: PayloadAction<boolean>) {
      state.domainsLoading = action.payload;
    },
    setSelectedDomain(state, action: PayloadAction<UasDomainDto | null>) {
      state.selectedDomain = action.payload;
    },
    setAdvisorsWithoutUas(state, action: PayloadAction<AdvisorDto[]>) {
      state.advisorsWithNoUasRelation = action.payload;
    },
    setDomains(state, action: PayloadAction<UasDomainDto[]>) {
      const domains = action.payload;
      const selectedDomain = state.selectedDomain?.id
        ? (domains.find((d) => d.id === state.selectedDomain!.id) ?? null)
        : null;
      const domainsForUas: { [id: number]: UasDomainDto[] } = {};
      for (const [key, value] of Object.entries(state.domainsForUas)) {
        domainsForUas[Number(key)] = (value as UasDomainDto[])
          .map((ud) => domains.find((d) => d.id === ud.id))
          .filter((ud): ud is UasDomainDto => !!ud);
      }
      state.domains = domains;
      state.selectedDomain = selectedDomain;
      state.domainsForUas = domainsForUas;
    },
    setDomainsForUas(state, action: PayloadAction<{ uas: UasDto; domains: UasDomainDto[] }>) {
      state.domainsForUas[action.payload.uas.id] = action.payload.domains;
    },
    setSelectedAdvisor(state, action: PayloadAction<AdvisorDto | null>) {
      state.selectedAdvisor = action.payload;
    },
  },
});

export const uasManagementReducer = slice.reducer;
export const uasManagementActions = slice.actions;

// Component-scoped actions factory
type ThunkFn<T = void> = (dispatch: any, getState: () => any) => Promise<T>;
type SyncThunkFn<T = void> = (dispatch: any, getState: () => any) => T;
const getAdvisorName = (advisor: AdvisorDto) => [advisor.firstName, advisor.familyName].filter(Boolean).join(' ') || 'advisor';

const initActions = (
  componentDispatch: any,
  uasClient: IUasClient,
  domainClient: IUasDomainClient,
  advisorClient: IAdvisorClient,
  accountClient: IAccountClient,
  advisorSelectionActions: AdvisorSelectionActions,
  refreshUas: () => Promise<UasDto[]>,
) => {
  const { actions } = slice;

  const isAdmin: () => SyncThunkFn<boolean> = () => (dispatch, getState) => {
    const roles = getState().auth.account?.roles ?? [];
    return hasAnyRole(roles, [RoleType.REGISTRATIONADMIN, RoleType.STUDENTADMIN]);
  };

  const refreshDomainsAsync = (): ThunkFn<UasDomainDto[]> => async (dispatch) => {
    const domains = (await domainClient.list()) ?? [];
    const sortedDomains = domains.sort((d1, d2) => ((d1.name ?? '') > (d2.name ?? '') ? 1 : -1));
    dispatch(actions.setDomains(sortedDomains));
    return sortedDomains;
  };

  const refreshAdvisorsAsync =
    (uas?: UasDto, changedAdvisor?: AdvisorDto): ThunkFn<AdvisorDto[]> =>
    async (dispatch) => {
      if (changedAdvisor?.uas) {
        for (const advisorUas of changedAdvisor.uas.filter((u) => u.id !== uas?.id)) {
          await advisorSelectionActions.loadAdvisorsAsync(advisorUas.id);
        }
      }

      if (uas) {
        const result = await advisorSelectionActions.loadAdvisorsAsync(uas.id);
        const updatedAdvisor = result.find((a) => a.id === changedAdvisor?.id);
        if ((!updatedAdvisor || !changedAdvisor?.uas?.length) && dispatch(isAdmin())) {
          const advisors = (await advisorClient.getAdvisorsWithNoUasRelation()) ?? [];
          dispatch(actions.setAdvisorsWithoutUas(advisors));
        }
        return result;
      }

      if (dispatch(isAdmin())) {
        const advisors = (await advisorClient.getAdvisorsWithNoUasRelation()) ?? [];
        dispatch(actions.setAdvisorsWithoutUas(advisors));
      }

      return [];
    };

  const loadAllUasAsync = (): ThunkFn<UasDto[]> => async (dispatch) => {
    dispatch(actions.setLoading(true));
    try {
      const result = await refreshUas();
      dispatch(actions.setLoading(false));
      return result;
    } catch (e) {
      dispatch(actions.setLoading(false));
      dispatch(addExceptionNotification(e, `An error occurred while loading uas!`));
      return [];
    }
  };

  const loadAdvisorsWithNoUasAsync = (): ThunkFn<AdvisorDto[]> => async (dispatch) => {
    dispatch(actions.setLoading(true));
    try {
      const advisors = (await advisorClient.getAdvisorsWithNoUasRelation()) ?? [];
      dispatch(actions.setAdvisorsWithoutUas(advisors));
      dispatch(actions.setLoading(false));
      return advisors;
    } catch (e) {
      dispatch(addExceptionNotification(e, `An error occurred while loading advisors.`));
      dispatch(actions.setLoading(false));
      return [];
    }
  };

  const loadAdvisorsAsync =
    (uas?: UasDto): ThunkFn<AdvisorDto[]> =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        const result = await dispatch(refreshAdvisorsAsync(uas));
        dispatch(actions.setLoading(false));
        return result;
      } catch (e) {
        dispatch(addExceptionNotification(e, `An error occurred while loading advisors.`));
        dispatch(actions.setLoading(false));
        return [];
      }
    };

  const loadAllDomainsAsync = (): ThunkFn<UasDomainDto[]> => async (dispatch) => {
    dispatch(actions.setDomainsLoading(true));
    try {
      const result = await dispatch(refreshDomainsAsync());
      dispatch(actions.setDomainsLoading(false));
      return result;
    } catch (e) {
      dispatch(actions.setDomainsLoading(false));
      dispatch(addExceptionNotification(e, `An error occurred while loading domains!`));
      return [];
    }
  };

  const loadDomainsForUasAsync =
    (uas: UasDto): ThunkFn<UasDomainDto[]> =>
    async (dispatch) => {
      if (!uas?.id) {
        return [];
      }
      dispatch(actions.setDomainsLoading(true));
      try {
        const result = (await domainClient.getForUas(uas.id)) ?? [];
        dispatch(actions.setDomainsForUas({ uas, domains: result }));
        dispatch(actions.setDomainsLoading(false));
        return result;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while loading domains!`));
        return [];
      }
    };

  const updateUasAsync =
    (uas: UasDto): ThunkFn<UasDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        await uasClient.update(uas);
        const result = await refreshUas();
        const updatedUas = result.find((u) => u.id === uas.id);
        dispatch(actions.setSelectedUas(updatedUas ?? null));
        dispatch(addSuccess(`Successfully updated uas ${uas.code}.`));
        dispatch(actions.setLoading(false));
        return updatedUas;
      } catch (e) {
        dispatch(actions.setLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while updating uas ${uas.code}`));
        return uas;
      }
    };

  const addUasAsync =
    (uas: UasDto): ThunkFn<UasDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        const id = await uasClient.add(uas);
        const result = await refreshUas();
        const updatedUas = result.find((u) => u.id === id);
        dispatch(actions.setSelectedUas(updatedUas ?? null));
        dispatch(addSuccess(`Successfully added uas ${uas.code}.`));
        dispatch(actions.setLoading(false));
        return updatedUas;
      } catch (e) {
        dispatch(actions.setLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while adding uas ${uas.code}`));
        return uas;
      }
    };

  const deleteUasAsync =
    (uas: UasDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        await uasClient.delete(uas.id);
        await refreshUas();
        dispatch(actions.setSelectedUas(null));
        dispatch(addSuccess(`Successfully deleted uas ${uas.code}.`));
        dispatch(actions.setLoading(false));
      } catch (e) {
        dispatch(actions.setLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while deleting uas ${uas.code}`));
      }
    };

  const saveUasAsync =
    (uas: UasDto): ThunkFn<UasDto | undefined> =>
    async (dispatch) => {
      if (uas.id) {
        return await dispatch(updateUasAsync(uas));
      }
      return await dispatch(addUasAsync(uas));
    };

  const addDomainAsync =
    (uas: UasDto, domain: UasDomainDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await domainClient.addToUas(uas.id, domain.id);
        const result = (await domainClient.getForUas(uas.id)) ?? [];
        dispatch(actions.setDomainsForUas({ uas, domains: result }));
        await dispatch(refreshDomainsAsync());
        dispatch(addSuccess(`Successfully added domain ${domain.name} to uas ${uas.code}.`));
        dispatch(actions.setDomainsLoading(false));
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while adding ${domain.name} to uas ${uas.code}.`));
      }
    };

  const removeDomainAsync =
    (uas: UasDto, domain: UasDomainDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await domainClient.removeFromUas(uas.id, domain.id);
        const result = (await domainClient.getForUas(uas.id)) ?? [];
        dispatch(actions.setDomainsForUas({ uas, domains: result }));
        await dispatch(refreshDomainsAsync());
        dispatch(addSuccess(`Successfully removed domain ${domain.name} from uas ${uas.code}.`));
        dispatch(actions.setDomainsLoading(false));
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(
          addExceptionNotification(e, `An error occurred while removing domain ${domain.name} to uas ${uas.code}.`),
        );
      }
    };

  const addNewDomainAsync =
    (domain: UasDomainDto): ThunkFn<UasDomainDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        const id = await domainClient.addDomain(domain);
        const result = await dispatch(refreshDomainsAsync());
        const addedDomain = result.find((d) => d.id === id);
        dispatch(actions.setSelectedDomain(addedDomain ?? null));
        dispatch(addSuccess(`Successfully added domain ${addedDomain?.name}`));
        dispatch(actions.setDomainsLoading(false));
        return addedDomain;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while adding domain ${domain.name}`));
        return domain;
      }
    };

  const updateDomainAsync =
    (domain: UasDomainDto): ThunkFn<UasDomainDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await domainClient.updateDomain(domain);
        const result = await dispatch(refreshDomainsAsync());
        const updatedDomain = result.find((d) => d.id === domain.id);
        dispatch(addSuccess(`Successfully updated domain ${updatedDomain?.name}`));
        dispatch(actions.setDomainsLoading(false));
        return updatedDomain;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while adding domain ${domain.name}`));
        return domain;
      }
    };

  const deleteDomainAsync =
    (domain: UasDomainDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await domainClient.deleteDomain(domain.id);
        await dispatch(refreshDomainsAsync());
        dispatch(addSuccess(`Successfully deleted domain ${domain.name}`));
        dispatch(actions.setDomainsLoading(false));
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(addExceptionNotification(e, `An error occurred while deleting domain ${domain.name}`));
      }
    };

  const saveDomainAsync =
    (domain: UasDomainDto): ThunkFn<UasDomainDto | undefined> =>
    async (dispatch) => {
      if (domain.id) {
        return await dispatch(updateDomainAsync(domain));
      } else {
        return await dispatch(addNewDomainAsync(domain));
      }
    };

  const addAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        const id = await advisorClient.add(advisor, uas?.id ?? 0);
        const advisors = await dispatch(refreshAdvisorsAsync(uas, advisor));
        const newAdvisor = advisors.find((a) => a.id === id);
        dispatch(actions.setSelectedAdvisor(newAdvisor ?? null));
        dispatch(
          addSuccess(`Successfully added new advisor '${getAdvisorName(advisor)}' to uas ${uas.code}.`),
        );
        dispatch(actions.setDomainsLoading(false));
        return newAdvisor;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while adding new advisor '${getAdvisorName(advisor)}' to uas ${uas.code}.`,
          ),
        );
        return advisor;
      }
    };

  const addUasRelationToAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await advisorClient.addUasRelation(advisor.id, uas.id);
        const advisors = await dispatch(refreshAdvisorsAsync(uas, advisor));
        const newAdvisor = advisors.find((a) => a.id === advisor.id);
        dispatch(actions.setSelectedAdvisor(newAdvisor ?? null));
        dispatch(
          addSuccess(`Successfully added advisor '${getAdvisorName(advisor)}' to uas ${uas.code}.`),
        );
        dispatch(actions.setDomainsLoading(false));
        return newAdvisor;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while adding advisor '${getAdvisorName(advisor)}' to uas ${uas.code}.`,
          ),
        );
        return advisor;
      }
    };

  const removeUasRelationFromAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setDomainsLoading(true));
      try {
        await advisorClient.removeUasRelation(advisor.id, uas.id);
        const advisors = await dispatch(refreshAdvisorsAsync(uas, advisor));
        const newAdvisor = advisors.find((a) => a.id === advisor.id);
        dispatch(actions.setSelectedAdvisor(newAdvisor ?? null));
        dispatch(
          addSuccess(`Successfully removed advisor '${getAdvisorName(advisor)}' from uas ${uas.code}.`),
        );
        dispatch(actions.setDomainsLoading(false));
        return newAdvisor;
      } catch (e) {
        dispatch(actions.setDomainsLoading(false));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while removing advisor '${getAdvisorName(advisor)}' from uas ${uas.code}.`,
          ),
        );
        return advisor;
      }
    };

  const updateAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      dispatch(actions.setLoading(true));
      try {
        await advisorClient.update(advisor);
        const advisors = await dispatch(refreshAdvisorsAsync(uas, advisor));
        const updatedAdvisor = advisors.find((a) => a.id === advisor.id);
        dispatch(actions.setSelectedAdvisor(updatedAdvisor ?? null));
        dispatch(addSuccess(`Successfully updated advisor '${getAdvisorName(advisor)}'.`));
        dispatch(actions.setLoading(false));
        return updatedAdvisor;
      } catch (e) {
        dispatch(actions.setLoading(false));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while updating advisor '${getAdvisorName(advisor)}'.`,
          ),
        );
        return advisor;
      }
    };

  const saveAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      if (advisor.id) {
        return await dispatch(updateAdvisorAsync(uas, advisor));
      }
      return await dispatch(addAdvisorAsync(uas, advisor));
    };

  const deleteAdvisorAsync =
    (uas: UasDto, advisor: AdvisorDto): ThunkFn<AdvisorDto | undefined> =>
    async (dispatch) => {
      const advisorName = getAdvisorName(advisor);
      dispatch(actions.setLoading(true));
      try {
        await advisorClient.delete(advisor.id, advisor.uas?.length ? uas?.id : null);
        const advisors = await dispatch(refreshAdvisorsAsync(uas, advisor));
        const updatedAdvisor = advisors.find((a) => a.id === advisor.id);
        dispatch(actions.setSelectedAdvisor(updatedAdvisor ?? null));
        dispatch(addSuccess(`Successfully deleted advisor '${advisorName}'.`));
        dispatch(actions.setLoading(false));
        return updatedAdvisor;
      } catch (e) {
        dispatch(actions.setLoading(false));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while deleting advisor '${advisorName}'.`,
          ),
        );
        return advisor;
      }
    };

  const emailAlreadyExistsAsync = async (userId: string, email: string) => {
    return await accountClient.checkEmail(userId, email);
  };

  return Object.freeze({
    loadAllUasAsync: async (): Promise<UasDto[]> => await componentDispatch(loadAllUasAsync()),
    loadDomainsForUasAsync: async (uas: UasDto): Promise<UasDomainDto[]> =>
      await componentDispatch(loadDomainsForUasAsync(uas)),
    loadAllDomainsAsync: async (): Promise<UasDomainDto[]> => await componentDispatch(loadAllDomainsAsync()),
    saveUasAsync: async (uas: UasDto): Promise<UasDto | undefined> => await componentDispatch(saveUasAsync(uas)),
    deleteUasAsync: async (uas: UasDto): Promise<void> => await componentDispatch(deleteUasAsync(uas)),
    selectUas: (uas: UasDto | null) => componentDispatch(actions.setSelectedUas(uas)),
    deleteDomainAsync: async (domain: UasDomainDto): Promise<void> =>
      await componentDispatch(deleteDomainAsync(domain)),
    saveDomainAsync: async (domain: UasDomainDto): Promise<UasDomainDto | undefined> =>
      await componentDispatch(saveDomainAsync(domain)),
    addDomainAsync: async (uas: UasDto, domain: UasDomainDto): Promise<void> =>
      await componentDispatch(addDomainAsync(uas, domain)),
    removeDomainAsync: async (uas: UasDto, domain: UasDomainDto): Promise<void> =>
      await componentDispatch(removeDomainAsync(uas, domain)),
    selectDomain: (domain: UasDomainDto | null) => componentDispatch(actions.setSelectedDomain(domain)),
    selectAdvisor: (advisor: AdvisorDto | null) => componentDispatch(actions.setSelectedAdvisor(advisor)),
    saveAdvisorAsync: async (uas: UasDto, advisor: AdvisorDto): Promise<AdvisorDto | undefined> =>
      await componentDispatch(saveAdvisorAsync(uas, advisor)),
    deleteAdvisorAsync: async (uas: UasDto, advisor: AdvisorDto): Promise<AdvisorDto | undefined> =>
      await componentDispatch(deleteAdvisorAsync(uas, advisor)),
    loadAdvisorsWithNoUasAsync: async (): Promise<AdvisorDto[]> =>
      await componentDispatch(loadAdvisorsWithNoUasAsync()),
    addUasRelationToAdvisorAsync: async (uas: UasDto, advisor: AdvisorDto): Promise<AdvisorDto | undefined> =>
      await componentDispatch(addUasRelationToAdvisorAsync(uas, advisor)),
    removeUasRelationFromAdvisorAsync: async (uas: UasDto, advisor: AdvisorDto): Promise<AdvisorDto | undefined> =>
      await componentDispatch(removeUasRelationFromAdvisorAsync(uas, advisor)),
    emailAlreadyExistsAsync,
    loadAdvisorsAsync: async (uas?: UasDto): Promise<AdvisorDto[]> => await componentDispatch(loadAdvisorsAsync(uas)),
  });
};

export type UserManagementActions = ReturnType<typeof initActions>;

export const uasManagementModule = {
  initActions(dispatch: any) {
    const authActions = authThunks;
    return initActions(
      dispatch,
      resolver.resolve<IUasClient>('IUasClient'),
      resolver.resolve<IUasDomainClient>('IUasDomainClient'),
      resolver.resolve<IAdvisorClient>('IAdvisorClient'),
      resolver.resolve<IAccountClient>('IAccountClient'),
      advisorSelectionModule.initActions(dispatch),
      async () => await dispatch(authActions.refreshAllowedUasThunk()).unwrap(),
    );
  },
  reducer: uasManagementReducer,
};
